import { Button, Select, Space, Spin } from 'antd';
import copy from 'copy-to-clipboard';
import { useCreateShareContentLink } from '@/common/hook/useShareContent';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import { FC, Fragment, MutableRefObject, useEffect, useRef, useState } from 'react';
import { useMethods } from '@lhb/hook';
import V2Drawer, { V2DrawerHandles } from '@/common/components/Feedback/V2Drawer';
import V2Title from '@/common/components/Feedback/V2Title';
import V2DetailItem from '@/common/components/Feedback/V2DetailItem';
import V2Empty from '@/common/components/Data/V2Empty';
import { getMallDecisionReportDetail, getMallDecisionReportFlowInfo, getMallDecisionReportMallSituation, getMallDecisionReportPointInfo, getMallDecisionReportBusinessInfo, getMallDecisionReportSmartDecision } from '@/common/api/expandStore/chancepoint';
import { adaptMallReportApis, emptyReport, fixtureScenario } from './adapters';
import { ReportData } from './types';
import MediaGallery from './components/MediaGallery';
import SectionNav from './components/SectionNav';
import MallSection from './components/MallSection';
import PointSection from './components/PointSection';
import TrafficSection from './components/TrafficSection';
import ReportActions from './components/ReportActions';
import LazyModule from './components/LazyModule';
import styles from './index.module.less';
import CommercialCondition, { CommercialConditionDetail } from './components/CommercialCondition';
import IntelligentDecision from './components/IntelligentDecision';
import ComparePointModal from './components/ComparePointModal';
import CompareDrawer from './components/CompareDrawer';
import { SmartDecisionData } from './components/IntelligentDecision/types';
import { mallReportShareContent, mallReportShareCopyText } from './sharePresentation';
import { useShareMetaTags } from '@/common/hook/useShareMetaTags';

// 2026-10-20 已确认范围：PC 改媒体区、商场名/地址、地图、租金判定展示及分享；计算/客流权限由后端修复。
// 投资总览、构成占比、三阶段计划、ROI 和 8 项 tooltip 改造已取消。

const BUSINESS_CARDS = [
  { label: '月租金', value: '12,000元', tone: 'orange' },
  { label: '租期(合同周期)', value: '24个月', tone: 'blue' },
  { label: '押金', value: '22,000元', tone: 'green' },
  { label: '商务类型(两者取高)', value: '租金+扣点', tone: 'purple' },
];

const FINANCIAL_SUMMARY = [
  { value: '29.6万', hint: '月预估销售额' },
  { value: '30.4%', hint: '净利率' },
  { value: '2.5个月', hint: '预计回本周期' },
];


type MallDecisionReportDrawerProps = { open: boolean; onClose?: () => void; chancePointId: number; shared?: boolean; shareParams?: any };

const MallDecisionReportDrawer: FC<MallDecisionReportDrawerProps> = ({ open, onClose, chancePointId, shared = false, shareParams }) => {
  const drawerWrapper: MutableRefObject<V2DrawerHandles | null> = useRef(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [container, setContainer] = useState<HTMLElement>();
  const [activeAnchor, setActiveAnchor] = useState('spot');
  const [data, setData] = useState<ReportData>(emptyReport);
  const [businessInfo, setBusinessInfo] = useState<CommercialConditionDetail>();
  const [smartDecision, setSmartDecision] = useState<SmartDecisionData>();
  const [scenario, setScenario] = useState<string | null>(!shared && process.env.NODE_ENV === 'development' ? fixtureScenario(window.location.search, true) : null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [moduleFailed, setModuleFailed] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [sharing, setSharing] = useState(false);
  // 对比点位弹窗状态，selectedIds 用于从对比抽屉再次打开时回显已选点位
  const [compareModal, setCompareModal] = useState<{ open: boolean; selectedIds: number[] }>({ open: false, selectedIds: [] });
  // 对比抽屉是否打开
  const [compareDrawerOpen, setCompareDrawerOpen] = useState(false);
  // 参与对比的机会点 ID，基准点位固定排在首位
  const [comparePointIds, setComparePointIds] = useState<number[]>([]);
  const { createShareContentLink } = useCreateShareContentLink();
  const request = useRef(0);
  const navigationScrollLock = useRef<number | null>(null);
  const headerRef = useRef<HTMLElement>(null);
  const navigationRef = useRef<HTMLDivElement>(null);

  const methods = useMethods({
    renderPermission(item: ReportData['permissions'][number]) {
      if (item.event === 'saasDecisionShare') {
        return (
          <div
            key={item.event}
            onClick={loading || failed || item.disabled || sharing ? undefined : methods.shareReport}
            style={{
              cursor: loading || failed || item.disabled || sharing ? 'not-allowed' : 'pointer',
              opacity: loading || failed || item.disabled || sharing ? 0.6 : 1
            }}
          >
            {sharing ? '分享中...' : item.name}
          </div>
        );
      }
      return <Button key={item.event} type='primary' disabled={loading || failed || item.disabled}>{item.name}</Button>;
    },
    handleClose() {
      methods.resetCompare();
      onClose?.();
    },
    /** 重置对比弹窗、对比抽屉与参与对比的点位 */
    resetCompare() {
      setCompareModal({ open: false, selectedIds: [] });
      setCompareDrawerOpen(false);
      setComparePointIds([]);
    },
    /** 打开对比点位弹窗，以当前机会点为基准 */
    openCompareModal() {
      setCompareModal({ open: true, selectedIds: [Number(chancePointId)] });
    },
    /** 从对比抽屉打开对比点位弹窗，回显当前已选点位 */
    openCompareModalFromDrawer() {
      setCompareModal({ open: true, selectedIds: comparePointIds });
    },
    /** 关闭对比点位弹窗并清空选择 */
    closeCompareModal() {
      setCompareModal({ open: false, selectedIds: [] });
    },
    /** 确认对比点位：关闭弹窗后打开对比抽屉 */
    confirmComparePoint(ids: number[]) {
      setComparePointIds(ids);
      setCompareModal({ open: false, selectedIds: [] });
      setCompareDrawerOpen(true);
    },
    /** 移除对比抽屉中的某个点位 */
    removeComparePoint(id: number) {
      setComparePointIds((current) => current.filter((item) => Number(item) !== Number(id)));
    },
    /** 关闭对比抽屉并清空本轮对比点位 */
    closeCompareDrawer() {
      setCompareDrawerOpen(false);
      setComparePointIds([]);
    },
    changeScenario(value: string) { setScenario(value); },
    /** 判定是否通过评估（租售比、回本周期、净利率三项指标） */
    getEvaluationPassed() {
      const summary = smartDecision?.summary;
      if (!summary) return false;
      const priceToRentRatio = Number(summary.priceToRentRatio);
      const paybackPeriod = Number(summary.paybackPeriod);
      const netProfitRate = Number(summary.netProfitRate);
      return (
        Number.isFinite(priceToRentRatio) && priceToRentRatio > 0 && priceToRentRatio < 20 &&
        Number.isFinite(paybackPeriod) && paybackPeriod > 0 && paybackPeriod < 12 &&
        Number.isFinite(netProfitRate) && netProfitRate > 15
      );
    },
    async shareReport() {
      if (sharing || !chancePointId) return;
      setSharing(true);
      try {
        const passed = methods.getEvaluationPassed();
        const shareContent = mallReportShareContent(
          data.name,
          data.businessDistrictType,
          smartDecision?.summary ? (passed ? '建议推进签约' : '不建议签约') : undefined,
        );
        if (shared) document.title = shareContent.title;
        const url = await createShareContentLink({
          shareUrl: '/share/mallreport',
          params: {
            id: chancePointId,
            shareTitle: shareContent.title,
            shareDescription: shareContent.description,
          },
        });
        if (!url) throw new Error('empty share link');
        if (copy(mallReportShareCopyText(data.name, url))) V2Message.success('链接已复制，可直接发送给你的朋友');
        else V2Message.error('复制失败，请重试');
      } catch {
        V2Message.error('分享链接生成失败，请重试');
      } finally {
        setSharing(false);
      }
    },
    retryModule() { setModuleFailed(false); },
    /**
     * 开发模拟数据场景下，商务条件与智能决策仍取真实接口
     *
     * 【新增子模块请注意】本报告的所有接口统一在本组件（父级）中请求，
     * 再通过 props 下发给子模块，子模块不要自己发请求。原因见下方
     * loadDetail 中 Promise.all 上的注释（同 URL 并发会被自动 cancel）。
     */
    loadExtraModules(version: number) {
      if (!chancePointId) return;
      Promise.all([
        getMallDecisionReportBusinessInfo({ id: chancePointId }),
        getMallDecisionReportSmartDecision({ id: chancePointId }),
      ]).then(([business, smart]: any[]) => {
        if (version !== request.current) return;
        setBusinessInfo(business || {});
        setSmartDecision(smart || {});
      }, (err: any) => { console.log(err); });
    },
    loadDetail() {
      const version = ++request.current;
      setData(emptyReport());
      setBusinessInfo(undefined);
      setSmartDecision(undefined);
      setFailed(false);
      setLoading(true);
      setModuleFailed(scenario === 'error');
      if (scenario && process.env.NODE_ENV === 'development') {
        methods.loadExtraModules(version);
        import('./development/fixtures').then(({ createFixture }) => {
          if (version !== request.current) return;
          setData(createFixture(scenario));
          setActiveAnchor(scenario === 'unmatched' || scenario === 'empty' ? 'spot' : 'mall');
        }, () => { if (version === request.current) setFailed(true); }).finally(() => { if (version === request.current) setLoading(false); });
        return;
      }
      if (!chancePointId) { setLoading(false); setFailed(true); return; }
      /**
       * 报告各模块的接口统一在此处请求，结果通过 props 下发给子模块。
       *
       * 【重要】请求层（src/common/request/instance.ts）默认开启了按 URL 去重的
       * 自动取消：同一个 URL 并发时，先发出的那次会被后发的 cancel 掉。而 React 的
       * effect 是子组件先于父组件执行，所以子模块若在自己内部再请求同一个接口，
       * 会先发出请求并随即被这里的请求 cancel，子模块只能拿到 cancel 异常，
       * 表现为「接口有返回但页面没数据」。
       *
       * 因此新增子模块时：
       * 1. 在这里补一个接口调用，把结果通过 props 传给子模块（推荐，与现有模块一致）；
       * 2. 确有需要在子模块内部单独请求时，该接口不要在这里重复调用；若无法避免重复，
       *    需给其中一方显式传 needCancel: false 关掉自动取消。
       */
      Promise.allSettled([
        getMallDecisionReportDetail({ id: chancePointId }),
        getMallDecisionReportMallSituation({ id: chancePointId }),
        getMallDecisionReportPointInfo({ id: chancePointId }),
        getMallDecisionReportFlowInfo({ id: chancePointId }),
        getMallDecisionReportBusinessInfo({ id: chancePointId }),
        getMallDecisionReportSmartDecision({ id: chancePointId }),
      ]).then((results) => {
        if (version !== request.current) return;
        const [detail, mall, point, flow, business, smart] = results.map(result => result.status === 'fulfilled' ? result.value : undefined);
        const report = adaptMallReportApis(detail, mall, point, flow, business, smart);
        setData(report);
        const detailData = detail?.data?.data || detail?.data || detail;
        setBusinessInfo({ ...business, actualMonthlyRent: detailData?.businessInfo?.monthExpense });
        setSmartDecision(smart);
        setActiveAnchor(report.mallMatched ? 'mall' : 'spot');
        if (results.some(result => result.status === 'rejected')) {
          V2Message.error('部分报告数据加载失败，请重新打开报告重试');
        }
      }, () => { if (version === request.current) setFailed(true); }).finally(() => { if (version === request.current) setLoading(false); });
    },
    syncContainer() {
      if (scrollContainerRef.current) setContainer(scrollContainerRef.current);
    },
    getAnchorOffset() {
      const navigation = navigationRef.current;
      const containerElement = scrollContainerRef.current;
      if (!navigation || !containerElement) return 12;
      // 以吸顶导航实际底部为基准，覆盖外层 padding、margin 和导航内容高度。
      return Math.max(0, navigation.getBoundingClientRect().bottom - containerElement.getBoundingClientRect().top) + 12;
    },
    scrollToAnchor(key: string) {
      setActiveAnchor(key);
      if (!container) return;
      if (navigationScrollLock.current !== null) window.clearTimeout(navigationScrollLock.current);
      navigationScrollLock.current = window.setTimeout(() => { navigationScrollLock.current = null; }, 500);
      // 等待两帧让 active 状态和 sticky 导航布局稳定后再计算位置。
      const align = function alignTarget() {
        const target = container.querySelector<HTMLElement>(`[data-report-anchor="${key}"]`);
        if (!target) return;
        // offsetTop is independent of sticky 元素的视口位置，避免异步模块渲染时 rect 基准漂移。
        let offsetTop = 0;
        let current: HTMLElement | null = target;
        while (current && current !== container) {
          offsetTop += current.offsetTop;
          current = current.offsetParent as HTMLElement | null;
        }
        const top = offsetTop - methods.getAnchorOffset();
        container.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
      };
      window.requestAnimationFrame(() => window.requestAnimationFrame(align));
      window.setTimeout(align, 350);
    },
    onScroll() {
      if (!container) return;
      setScrolled(container.scrollTop > 8);
      if (navigationScrollLock.current !== null) return;
      const sections = Array.from(container.querySelectorAll<HTMLElement>('[data-report-anchor]'));
      const top = container.getBoundingClientRect().top + methods.getAnchorOffset() + 2;
      let current = sections[0]?.dataset.reportAnchor;
      sections.forEach(section => { if (section.getBoundingClientRect().top <= top) current = section.dataset.reportAnchor; });
      if (container.scrollTop + container.clientHeight >= container.scrollHeight - 2) current = sections[sections.length - 1]?.dataset.reportAnchor;
      if (current) setActiveAnchor(current);
    },
    renderBusinessCard(item: typeof BUSINESS_CARDS[number]) {
      return (
        <div className={styles.businessCard} key={item.label}>
          <div className={`${styles.businessIcon} ${styles[`businessIcon${item.tone}`] || ''}`}></div>
          <div><strong>{item.value}</strong><span>{item.label}</span></div>
        </div>
      );
    },

    renderSimilarStoreRow(item: string[], index: number) {
      return (
        <tr key={item[0]}>
          <td className={styles.linkCell}>{item[0]}</td><td>{item[1]}</td><td className={index === 0 ? styles.warningCell : ''}>{item[2]}</td><td>{item[3]}</td><td>{item[4]}</td><td>{item[5]}</td>
        </tr>
      );
    },

    renderSynergyRow(item: string[]) {
      return <tr key={item[0]}><td>{item[0]}</td><td>{item[1]}</td><td>{item[2]}</td><td>{item[3]}</td></tr>;
    },

    renderFinancialSummary(item: typeof FINANCIAL_SUMMARY[number]) {
      return <div className={styles.financialSummaryCard} key={item.hint}><strong>{item.value}</strong><span>{item.hint}</span></div>;
    },

  });

  const fallbackShareContent = mallReportShareContent(
    data.name,
    data.businessDistrictType,
    smartDecision?.summary ? (methods.getEvaluationPassed() ? '建议推进签约' : '不建议签约') : undefined,
  );
  useShareMetaTags(
    shared ? (shareParams?.shareTitle || fallbackShareContent.title) : undefined,
    shared ? (shareParams?.shareDescription || fallbackShareContent.description) : undefined,
  );

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(methods.syncContainer, 0);
    methods.loadDetail();
    return () => { window.clearTimeout(timer); request.current += 1; };
  }, [open, chancePointId, scenario]);
  useEffect(() => {
    if (!container || !open) return;
    methods.onScroll();
    container.addEventListener('scroll', methods.onScroll, { passive: true });
    return () => { container.removeEventListener('scroll', methods.onScroll); };
  }, [container, open]);

  // 报告抽屉关闭时同步清理对比弹窗与对比抽屉
  useEffect(() => {
    if (!open) methods.resetCompare();
  }, [open]);

  // Shared markup is intentionally kept in one fragment for drawer and standalone routes.
  // 商场名单独展示；空媒体仍挂载分类与空态，摘要导航始终跟在媒体区下方。
  // eslint-disable-next-line indent
  const content = <>
    <header ref={headerRef} className={`${styles.reportHeader} ${scrolled ? styles.reportHeaderScrolled : ''}`}>
      <div>
        <V2Title type='H2' text={(data.mallName && (data.mallName + '-')) + data.name || '机会点决策报告'} bold />
        <span className={styles.reportMeta}>
          <span>{data.address || '地址待补充'}</span>
          <span>城市 · {data.city || '城市待补充'}</span>
          {data.mallName && <span>商场 · {data.mallName}</span>}
          <span>面积 · {data.survey[0]?.value ?? '面积待补充'}</span>
        </span>
      </div>
      {!shared && (
        <div className={styles.reportActions}>
          <Button
            onClick={methods.openCompareModal}
            disabled={!chancePointId}
          >
            对比
          </Button>
          {data.permissions.map(methods.renderPermission)}
        </div>
      )}
    </header>
    <Spin spinning={loading} wrapperClassName={styles.reportLoading}>
      <div ref={scrollContainerRef} className={styles.reportScrollContainer}>
        <div className={styles.reportCanvasOuter}>
          <main className={styles.reportCanvas}>
            {scenario && <Space style={{ padding: '12px 0', flexWrap: 'wrap' }}>
              <V2DetailItem moduleType='easy' value='开发模拟数据' />
              <ReportActions key={scenario} development onPreview={methods.shareReport} />
              <Select aria-label='开发场景' value={scenario} onChange={methods.changeScenario} style={{ width: 180 }} options={[
                { value: 'normal', label: '正常 / 多图多视频' }, { value: 'empty', label: '全空' },
                { value: 'partial', label: '部分缺失' }, { value: 'zero', label: '真实0值' },
                { value: 'long', label: '长文本' }, { value: 'unmatched', label: '商场未匹配' },
                { value: 'error', label: '模块失败' }, { value: 'video', label: '仅视频' },
              ]} />
            </Space>}
            {loading ? null : failed ? <div>
              <V2Empty type='fail' customTip='报告数据加载失败' /><Button onClick={methods.loadDetail}>重试</Button>
            </div> : <Fragment key={scenario || chancePointId}>
              <MediaGallery data={data.media} />
              <div ref={navigationRef} className={styles.reportTabsSticky}>
                <div className={`${styles.reportTabsWrap} ${scrolled ? styles.reportTabsScrolled : ''}`}>
                  <SectionNav data={data} activeKey={activeAnchor} onChange={methods.scrollToAnchor} />
                </div>
              </div>
              {data.mallMatched && <div data-report-anchor='mall'><LazyModule root={container} active={activeAnchor === 'mall'}>
                {moduleFailed ? <><V2Title type='H2' text='商场情况' /><V2Empty type='fail' /><Button onClick={methods.retryModule}>重试商场数据</Button></> : <MallSection data={data.mall} />}
              </LazyModule></div>}
              <div data-report-anchor='spot'><LazyModule root={container} active={activeAnchor === 'spot'}><PointSection data={data} /></LazyModule></div>
              <div data-report-anchor='traffic'><LazyModule root={container} active={activeAnchor === 'traffic'}><TrafficSection data={data.traffic} /></LazyModule></div>
            </Fragment>}
            {/* 商务条件 */}
            <div data-report-anchor='business'>
              <CommercialCondition detail={businessInfo} />
            </div>
            {/* 智能决策 */}
            <div data-report-anchor='smart'>
              <IntelligentDecision
                decisionData={smartDecision}
                commercialConditionDetail={businessInfo}
                mallLevel={data.overview?.projectLevel}
                shared={shared}
              />
            </div>
          </main>
        </div>
      </div>
    </Spin>
  </>;

  // eslint-disable-next-line indent
  return shared ? <div className={styles.sharedReport}>{content}</div> : (
    <>
      <V2Drawer open={open} onClose={methods.handleClose} destroyOnClose bodyStyle={{ padding: 0 }} contentWrapperStyle={{ width: '1100px' }} onRef={drawerWrapper} className={styles.customDrawer}>
        {content}
      </V2Drawer>
      {/* 对比点位弹窗 */}
      {compareModal.open && (
        <ComparePointModal
          chancePointId={chancePointId}
          selectedIds={compareModal.selectedIds}
          onCancel={methods.closeCompareModal}
          onOk={methods.confirmComparePoint}
        />
      )}
      {/* 对比抽屉 */}
      {compareDrawerOpen && (
        <CompareDrawer
          open={compareDrawerOpen}
          chancePointId={chancePointId}
          pointIds={comparePointIds}
          onClose={methods.closeCompareDrawer}
          onRemove={methods.removeComparePoint}
          onAdd={methods.openCompareModalFromDrawer}
        />
      )}
    </>
  );
};

export default MallDecisionReportDrawer;
